import { jwtDecode } from 'jwt-decode';
import { firstValueFrom } from 'rxjs';
import { Injectable, NgZone, computed, effect, inject, signal } from '@angular/core';
import { Client, IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { Notification } from '../types/notification.types';

/**
 * WebSocket (STOMP over SockJS) du centre de notifications.
 * Cf. `fronts/WEBSOCKET_NOTIFICATIONS.md`.
 *
 * Auth = Option A : le JWT voyage dans le header du frame STOMP `CONNECT`
 * (pas sur le handshake HTTP — impossible côté navigateur avec SockJS).
 *
 * Cycle de vie : `start()` est idempotent et appelé par le composant cloche
 * (toujours monté dans le shell authentifié). Un `effect` interne surveille
 * `AuthService.accessToken` et reconnecte automatiquement avec le token frais
 * après un refresh. `stop()` coupe tout (au logout).
 */
@Injectable({ providedIn: 'root' })
export class NotificationSocketService {
  private readonly auth = inject(AuthService);
  /** Les callbacks STOMP s'exécutent hors zone Angular → on réinjecte les
   *  mises à jour de signaux dans la zone pour déclencher la change detection
   *  (sinon le badge ne bouge qu'au prochain tick, ex. à l'ouverture du menu). */
  private readonly zone = inject(NgZone);

  private client?: Client;
  /** Dernier token avec lequel on s'est connecté — détecte les refreshs. */
  private connectedWithToken: string | null = null;
  /** Activé par `start()` ; gate la (re)connexion pilotée par l'effect. */
  private readonly active = signal(false);

  private readonly _connected = signal(false);
  private readonly _unreadCount = signal(0);
  /** Dernière notif reçue en temps réel — les consommateurs réagissent dessus. */
  private readonly _incoming = signal<Notification | null>(null);
  /** Dernière notif marquée lue côté serveur (ack WS). */
  private readonly _readAck = signal<Notification | null>(null);

  readonly connected = this._connected.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly incoming = this._incoming.asReadonly();
  readonly readAck = this._readAck.asReadonly();
  readonly hasUnread = computed(() => this._unreadCount() > 0);

  constructor() {
    // Reconnexion automatique sur (dé)connexion logique ou refresh de token.
    effect(() => {
      const token = this.auth.accessToken();
      const wanted = this.active();

      if (!wanted || !token) {
        this.teardown();
        return;
      }
      // Token rafraîchi alors qu'on était connecté → reconnecter au frais.
      if (this.client && token !== this.connectedWithToken) {
        this.reconnect(token);
        return;
      }
      if (!this.client) {
        this.openConnection(token);
      }
    });
  }

  /** Démarre la connexion (idempotent). À appeler depuis la cloche. */
  start(): void {
    this.active.set(true);
  }

  /** Coupe la connexion (au logout). */
  stop(): void {
    this.active.set(false);
  }

  /** Permet au composant de bootstrapper le badge depuis le REST. */
  setUnreadCount(n: number): void {
    this._unreadCount.set(Math.max(0, n));
  }

  /** Décrémente localement le badge (ex. après un markRead REST optimiste). */
  decrementUnread(by = 1): void {
    this._unreadCount.update((c) => Math.max(0, c - by));
  }

  /** Marquer une notif lue via WS (alternative au REST). */
  markRead(notificationId: number): void {
    this.client?.publish({
      destination: '/nawamapp/read',
      body: String(notificationId),
    });
  }

  // ── Internes ────────────────────────────────────────────────────────────

  private openConnection(token: string): void {
    const client = new Client({
      // SockJS comme transport ; PAS de header sur le handshake (limite navigateur).
      webSocketFactory: () =>
        new SockJS(`${environment.gatewayUrl}/notification/ws`),
      // Token sur le frame STOMP CONNECT (Option A).
      beforeConnect: async () => {
        const fresh = await this.freshToken();
        if (!fresh) { void client.deactivate(); return; }
        client.connectHeaders = { Authorization: `Bearer ${fresh}` };
        this.connectedWithToken = fresh;
      },
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      onConnect: () => {
        this.zone.run(() => this._connected.set(true));
        // Nouvelle notif → on l'expose pour la liste. Le badge est mis à jour
        // par le message `/count` que le serveur pousse juste après.
        client.subscribe('/user/nawamqueue/notifications', (m: IMessage) => {
          const notif = this.safeParse(m.body);
          if (notif) this.zone.run(() => this._incoming.set(notif));
        });
        // Compteur de non-lus poussé par le serveur (countNotMe, scopé userId)
        // → source de vérité temps réel du badge.
        client.subscribe('/user/nawamqueue/notifications/count', (m: IMessage) => {
          const raw = (m.body ?? '').trim();
          if (!raw) return; // corps vide → on ignore (garde la valeur courante)
          const n = Number(raw);
          if (Number.isFinite(n)) this.zone.run(() => this._unreadCount.set(Math.max(0, n)));
        });
        // Accusé de lecture → maj item (le badge suit via `/count`).
        client.subscribe('/user/nawamqueue/notifications/read', (m: IMessage) => {
          const notif = this.safeParse(m.body);
          if (notif) this.zone.run(() => this._readAck.set(notif));
        });
      },
      onDisconnect: () => this.zone.run(() => this._connected.set(false)),
      onWebSocketClose: () => this.zone.run(() => this._connected.set(false)),
      onStompError: (frame) =>
        console.error('[notif-ws] STOMP error', frame.headers['message']),
    });
    this.client = client;
    this.connectedWithToken = token;
    client.activate();
  }

  private reconnect(token: string): void {
    // Deactivate proprement puis ré-ouvre avec le token frais.
    const prev = this.client;
    this.client = undefined;
    this._connected.set(false);
    void prev?.deactivate();
    this.openConnection(token);
  }

  private teardown(): void {
    if (!this.client) return;
    const prev = this.client;
    this.client = undefined;
    this.connectedWithToken = null;
    this._connected.set(false);
    void prev.deactivate();
  }

  private safeParse(body: string): Notification | null {
    try {
      return JSON.parse(body) as Notification;
    } catch {
      return null;
    }
  }
  /**
   * Token à poser sur le CONNECT. Appelé par STOMP **avant chaque (re)connexion** : les
   * `connectHeaders` figés à la création rejouaient un access token expiré à chaque tentative
   * (toutes les 5 s, indéfiniment — logs notification-service « JWT invalide — Jwt expired »,
   * recette 2026-10-24). Si le token courant expire dans moins de 30 s, on le rafraîchit d'abord ;
   * sans token valide, on arrête le client au lieu de boucler.
   */
  private async freshToken(): Promise<string | null> {
    const current = this.auth.accessToken();
    if (current && !expiringSoon(current)) return current;
    try { await firstValueFrom(this.auth.refresh()); } catch { return null; }
    return this.auth.accessToken();
  }
}

/** Vrai si le JWT est expiré ou expire dans moins de 30 s (ou illisible). */
function expiringSoon(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return !exp || exp * 1000 - Date.now() < 30_000;
  } catch {
    return true;
  }
}
